// Pure view maths for the canvas: screen = world × zoom + offset.
export interface View { ox: number; oy: number; zoom: number }
export interface Rect { x: number; y: number; w: number; h: number }

export const MIN_ZOOM = 0.2;
export const MAX_ZOOM = 2.5;

export const screenToWorld = (v: View, sx: number, sy: number) => ({ x: (sx - v.ox) / v.zoom, y: (sy - v.oy) / v.zoom });
export const worldToScreen = (v: View, wx: number, wy: number) => ({ x: wx * v.zoom + v.ox, y: wy * v.zoom + v.oy });

/** Zooms by `factor` keeping the world point under (sx, sy) fixed on screen. */
export function zoomAt(v: View, sx: number, sy: number, factor: number): View {
  const zoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, v.zoom * factor));
  const w = screenToWorld(v, sx, sy);
  return { zoom, ox: sx - w.x * zoom, oy: sy - w.y * zoom };
}

export const intersects = (a: Rect, b: Rect) => a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;

export function normRect(x1: number, y1: number, x2: number, y2: number): Rect {
  return { x: Math.min(x1, x2), y: Math.min(y1, y2), w: Math.abs(x2 - x1), h: Math.abs(y2 - y1) };
}

/** World rectangle visible in a viewport of the given size, grown by `margin` screen pixels. */
export function visibleRect(v: View, width: number, height: number, margin = 300): Rect {
  const a = screenToWorld(v, -margin, -margin);
  const b = screenToWorld(v, width + margin, height + margin);
  return normRect(a.x, a.y, b.x, b.y);
}

/** A view that shows all rectangles, centred, never zoomed in past `maxZoom`. */
export function fitView(rects: Rect[], width: number, height: number, padding = 60, maxZoom = 1): View {
  if (!rects.length) return { ox: width / 2, oy: height / 3, zoom: 1 };
  const x1 = Math.min(...rects.map((r) => r.x));
  const y1 = Math.min(...rects.map((r) => r.y));
  const x2 = Math.max(...rects.map((r) => r.x + r.w));
  const y2 = Math.max(...rects.map((r) => r.y + r.h));
  const zoom = Math.max(MIN_ZOOM, Math.min(maxZoom, (width - 2 * padding) / Math.max(1, x2 - x1), (height - 2 * padding) / Math.max(1, y2 - y1)));
  return { zoom, ox: (width - (x2 - x1) * zoom) / 2 - x1 * zoom, oy: (height - (y2 - y1) * zoom) / 2 - y1 * zoom };
}

export const ESTIMATED_CARD_HEIGHT = 160;
